/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { colors } from '../../studio/constants/theme';

/** Route segment for Agentic Prompt Management tab (matches tenant tab config). */
export const AGENTIC_PROMPT_MGMT_ROUTE = 'agentic/prmptmgmt';

/** Route segment for legacy prompt management tab (matches tenant tab config). */
export const LEGACY_PROMPT_MGMT_ROUTE = 'promptmgt';

/** Toggle values for legacy ↔ agentic prompt module switcher (tenant tab config). */
export const MODULE_SWITCHER_VALUE_LEGACY = 'legacy';
export const MODULE_SWITCHER_VALUE_AGENTIC = 'agentic';

/** Visual variant for PromptModuleSwitcher (styling on agentic vs legacy screens). */
export const MODULE_SWITCHER_VARIANT = Object.freeze({
  LEGACY: MODULE_SWITCHER_VALUE_LEGACY,
  AGENTIC: MODULE_SWITCHER_VALUE_AGENTIC
});

export const MODULE_SWITCHER_VARIANTS = Object.freeze(Object.values(MODULE_SWITCHER_VARIANT));

export const buildPromptListPath = (tenantName) => `/aiadmin/${tenantName}/${AGENTIC_PROMPT_MGMT_ROUTE}`;

export const buildPromptDetailPath = (tenantName, promptRef, version) => {
  const base = `${buildPromptListPath(tenantName)}/${encodeURIComponent(promptRef)}`;
  if (version) {
    return `${base}?version=${encodeURIComponent(version)}`;
  }
  return base;
};

export const VIEW_TYPE_PROMPT_MANAGEMENT = 'promptManagement';

export const PROMPT_MGMT_PANEL = Object.freeze({
  CATALOGUE: 'catalogue',
  WORKSPACE: 'workspace'
});

export const PROMPT_MGMT_ACTIONS = Object.freeze({
  CREATE_PROMPT: 'createPrompt',
  SAVE_PROMPT: 'savePrompt',
  SAVE_PROMPT_VERSION: 'savePromptVersion',
  CREATE_NEW_VERSION: 'createNewVersion',
  SELECT_VERSION: 'selectVersion',
  VIEW_ASSOCIATED_GRAPHS: 'viewAssociatedGraphs'
});

export const PROMPT_WORKSPACE_TAB = Object.freeze({
  EDITOR: 'editor',
  HISTORY: 'history',
  INFO: 'info',
  VERSIONS: 'versions'
});

export const PROMPT_WORKSPACE_TAB_IDS = Object.freeze(Object.values(PROMPT_WORKSPACE_TAB));

/** Segment types for prompt history before/after text diffs. */
export const HISTORY_DIFF_SEGMENT_TYPE = Object.freeze({
  EQUAL: 'equal',
  DELETE: 'delete',
  INSERT: 'insert'
});

export const HISTORY_DIFF_SEGMENT_TYPES = Object.freeze(Object.values(HISTORY_DIFF_SEGMENT_TYPE));

export const CATALOGUE_SEARCH_DEBOUNCE_MS = 500;

/** Minimum typed characters before catalogue search is sent to GET /v2/prompts/catalogue. */
export const CATALOGUE_SEARCH_MIN_CHARS = 3;

export const CATALOGUE_DEFAULT_PAGE_SIZE = 50;
export const CATALOGUE_MAX_PAGE_SIZE = 100;

export const PROMPT_CATALOGUE_TITLE = 'agenticPromptMgmt.CATALOGUE_TITLE';

export const PROMPT_CATALOGUE_SEARCH_PLACEHOLDER = 'agenticPromptMgmt.CATALOGUE_SEARCH_PLACEHOLDER';

export const PROMPT_CATALOGUE_DEFAULT_SUBTITLE = 'agenticPromptMgmt.CATALOGUE_DEFAULT_SUBTITLE';

/** Info tab fields rendered with monospace typography. */
export const PROMPT_INFO_MONO_FIELD_NAMES = Object.freeze(['promptRef', 'promptVersionRef', 'id', 'version']);

export const PROMPT_INFO_METADATA_TITLE = 'agenticPromptMgmt.INFO_METADATA_TITLE';
export const PROMPT_INFO_METADATA_SUBTITLE = 'agenticPromptMgmt.INFO_METADATA_SUBTITLE';

export const PROMPT_CATALOGUE_FRESHNESS_STATUS = Object.freeze({
  RECENT: 'recent',
  MONTH: 'month',
  OLD: 'old'
});

export const PROMPT_CATALOGUE_FRESHNESS_COLORS = Object.freeze({
  [PROMPT_CATALOGUE_FRESHNESS_STATUS.RECENT]: colors.promptCatalogue.freshness.recent,
  [PROMPT_CATALOGUE_FRESHNESS_STATUS.MONTH]: colors.promptCatalogue.freshness.month,
  [PROMPT_CATALOGUE_FRESHNESS_STATUS.OLD]: colors.promptCatalogue.freshness.old
});

export const PROMPT_CATALOGUE_FRESHNESS_LEGEND = Object.freeze([
  { status: PROMPT_CATALOGUE_FRESHNESS_STATUS.RECENT, labelKey: 'agenticPromptMgmt.CATALOGUE_FRESHNESS_RECENT' },
  { status: PROMPT_CATALOGUE_FRESHNESS_STATUS.MONTH, labelKey: 'agenticPromptMgmt.CATALOGUE_FRESHNESS_MONTH' },
  { status: PROMPT_CATALOGUE_FRESHNESS_STATUS.OLD, labelKey: 'agenticPromptMgmt.CATALOGUE_FRESHNESS_OLD' }
]);

/** Graph node customType values shown in the prompt management grid (legacy). */
export const AGENT_PROMPT_NODE_TYPES = Object.freeze(['agent', 'supervisor']);

export const VERSION_SELECT_MENU_MAX_HEIGHT = 300;

/** i18n keys for prompt version API errors (shared with Studio promptManagement.*). */
export const PROMPT_MANAGEMENT_LANG_KEYS = Object.freeze({
  ERROR_VERSIONS: 'promptManagement.ERROR_VERSIONS',
  ERROR_VERSION: 'promptManagement.ERROR_VERSION',
  ERROR_SAVE: 'promptManagement.ERROR_SAVE',
  ERROR_CATALOGUE: 'agenticPromptMgmt.ERROR_CATALOGUE',
  ERROR_CREATE: 'agenticPromptMgmt.ERROR_CREATE',
  ERROR_HISTORY: 'agenticPromptMgmt.ERROR_HISTORY',
  ERROR_NAME_AVAILABILITY: 'agenticPromptMgmt.ERROR_NAME_AVAILABILITY',
  ERROR_ASSOCIATED_GRAPHS: 'agenticPromptMgmt.ERROR_ASSOCIATED_GRAPHS',
  REQUEST_TIMEOUT: 'promptManagement.REQUEST_TIMEOUT'
});
